import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * ═══ WHAT A PARENT IS BEING ASKED ═══
 *
 * One question, per child, about face recognition — and the answer can be changed at any time.
 *
 * ⚠ THE DEFAULT IS NO ANSWER, NOT "YES". Nothing on this page is pre-ticked and there is no
 * pre-selected button. A consent screen with the agreeable option already chosen collects taps, not
 * consent, and it is the single thing most likely to make this feature indefensible later.
 *
 * ⚠ WITHDRAWING IS AS EASY AS AGREEING — same page, same size, no reason field and no confirmation
 * that argues. A parent taking back permission for their child's biometrics does not owe the school
 * an explanation, and a screen that asks for one is a screen that discourages the answer.
 *
 * ⚠ IT SAYS WHAT HAPPENS IF THEY SAY NO, in the same breath as the question. A parent who cannot
 * tell whether refusing will disadvantage their child will agree out of caution, and that is not
 * agreement.
 */

interface Child { id: string; fullName: string; admissionNumber: string; classLabel: string | null }

interface Consent {
  studentId: string;
  state: 'NotAsked' | 'Granted' | 'Declined' | 'Withdrawn';
  channel: string;
  guardianName: string | null;
  answeredAt: string;
  withdrawnAt: string | null;
  expiresOn: string | null;
  effective: boolean;
  awaitingTemplateDeletion: boolean;
  enrolled: boolean;
}

/** ⚠ "7/2/2026" is 7 February to a parent in Mangalore and 2 July to the person who wrote it.
 *  Spelled month, always — a consent record is exactly the wrong place for an ambiguous date. */
const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

export function FamilyPermissionsPage() {
  const [children, setChildren] = useState<Child[]>([]);
  const [consents, setConsents] = useState<Record<string, Consent>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadConsent = useCallback(async (studentId: string) => {
    try {
      const c = await api.get<Consent>(`/api/portal/face-consent/${studentId}`);
      setConsents((m) => ({ ...m, [studentId]: c }));
    } catch { /* a child with no answer yet is the normal case */ }
  }, []);

  useEffect(() => {
    api.get<Child[]>('/api/portal/children')
      .then((xs) => { setChildren(xs); xs.forEach((c) => void loadConsent(c.id)); })
      .catch(() => setChildren([]));
  }, [loadConsent]);

  const answer = async (studentId: string, granted: boolean) => {
    setBusy(studentId); setError(null);
    try {
      const c = await api.post<Consent>(`/api/portal/face-consent/${studentId}`, { granted });
      setConsents((m) => ({ ...m, [studentId]: c }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save your answer.');
    } finally { setBusy(null); }
  };

  const withdraw = async (studentId: string) => {
    setBusy(studentId); setError(null);
    try {
      const c = await api.post<Consent>(`/api/portal/face-consent/${studentId}/withdraw`, {});
      setConsents((m) => ({ ...m, [studentId]: c }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save your answer.');
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>My family</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Permissions</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Things the school needs your agreement for. You can change any answer here at any time.
        </p>
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 14, borderLeft: '3px solid var(--danger, #c0392b)' }}>
          {error}
        </div>
      )}

      {/* ⚠ SAID ONCE, IN FULL, ABOVE THE CHILDREN. Repeating it per child produced a page of
          identical paragraphs that a parent scrolls past — see the note beside each child below. */}
      {children.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 640 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Marking attendance by face</div>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 0, marginBottom: 8 }}>
            The school would like to mark attendance by recognising your child's face. The class
            teacher holds up a phone at the classroom door, it recognises who is there, and the
            register is done in seconds instead of minutes.
          </p>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 0, marginBottom: 8 }}>
            <strong>If you say no, nothing changes for your child.</strong> Their teacher marks them
            from the register exactly as today, and nobody is told what you answered except the
            school office.
          </p>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 0 }}>
            What is stored is a mathematical pattern of the face, not photographs. It is deleted if
            you change your mind, and you can change your mind at any time on this page.
          </p>
        </div>
      )}

      {children.length === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
          No children are linked to your account yet.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children.map((child) => {
          const c = consents[child.id];
          const state = c?.state ?? 'NotAsked';
          const working = busy === child.id;
          return (
            <div key={child.id} className="card" style={{ padding: 18, maxWidth: 640 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{child.fullName}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
                {child.classLabel ?? 'No class'} · {child.admissionNumber}
              </div>

              {/* ⚠ ONE LINE, NOT THE WHOLE EXPLANATION AGAIN. The full text is above, once. Three
                  children used to mean the same four paragraphs three times, and a wall of repeated
                  text is a wall nobody reads — including the paragraph that says refusing is
                  harmless, which is the one that most needs reading.

                  ⚠ BUT IT IS NOT NOTHING, EITHER. The consequence of saying no is restated beside
                  the buttons, because that is where the decision is actually made and a parent who
                  scrolled past the explanation must still see it. */}
              {/* No heading here: the card above is the heading, and repeating "Marking attendance
                  by face" once per child was four identical bold lines on one screen. */}
              <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 0, marginBottom: 12 }}>
                Recognise {child.fullName.split(' ')[0]} at the classroom door and mark them present.
                Saying no changes nothing for them.
              </p>

              {state === 'Granted' && c?.effective ? (
                <div>
                  <div style={{ fontSize: 13, marginBottom: 10 }}>
                    You agreed{c.guardianName ? ` as ${c.guardianName}` : ''} on {when(c.answeredAt)}.
                    {c.enrolled ? ' Their face has been enrolled.' : ' Their face has not been enrolled yet.'}
                  </div>
                  {/* Same size, same page, no argument. */}
                  <button className="btn" disabled={working} onClick={() => withdraw(child.id)}>
                    {working ? 'Saving…' : 'Withdraw my agreement'}
                  </button>
                </div>
              ) : (
                <div>
                  {state === 'Declined' && (
                    <div style={{ fontSize: 13, marginBottom: 10, color: 'var(--ink-2)' }}>
                      You said no on {when(c!.answeredAt)}. That is recorded and nothing further is
                      needed.
                    </div>
                  )}
                  {state === 'Withdrawn' && (
                    <div style={{ fontSize: 13, marginBottom: 10, color: 'var(--ink-2)' }}>
                      You withdrew your agreement on {when(c!.withdrawnAt ?? c!.answeredAt)}.
                      {c!.awaitingTemplateDeletion
                        ? ' The stored pattern is queued for deletion.'
                        : ' The stored pattern has been deleted.'}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {/* ⚠ NEITHER BUTTON IS THE ACCENT COLOUR AND NEITHER IS PRE-SELECTED. Making
                        "yes" the bright one is a nudge, and a nudge on a biometrics consent form is
                        the thing that makes the whole record worthless. */}
                    <button className="btn" disabled={working} onClick={() => answer(child.id, true)}>
                      {working ? 'Saving…' : 'Yes, I agree'}
                    </button>
                    <button className="btn" disabled={working} onClick={() => answer(child.id, false)}>
                      No, I do not
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
